import React, { useState, useEffect } from "react";
import { CheckCircle2, Lock, Save, RefreshCw, AlertCircle } from "lucide-react";
import { fetchCategorySettings, updateCategorySettings } from "../system/settingsApi";

export const AuthenticationSettingsPage: React.FC = () => {
  const [sso, setSso] = useState({
    saml_enabled: false,
    google_oauth: true,
    microsoft_entra: false,
    password_reset_mail: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchCategorySettings("authentication");
      if (res.data) {
        setSso((prev) => ({ ...prev, ...res.data }));
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to load authentication settings.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      await updateCategorySettings("authentication", sso);
      setSaved(true);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 text-slate-400 text-xs">
        <RefreshCw className="w-5 h-5 animate-spin mr-2 text-indigo-400" />
        <span>Loading authentication settings...</span>
      </div>
    );
  }

  return (
    <div className="p-5 bg-slate-900/90 border border-slate-800 rounded-xl shadow-md text-xs font-sans space-y-4">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-950/60 border border-red-800 text-red-300 rounded-lg text-xs">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-center justify-between pb-3 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <Lock className="w-5 h-5 text-indigo-400" />
          <h2 className="text-base font-bold text-slate-100">Single Sign-On (SSO) & Authentication Providers</h2>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold rounded-lg shadow-md transition-colors text-xs"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin text-white" />
              <span>Saving...</span>
            </>
          ) : saved ? (
            <>
              <CheckCircle2 className="w-4 h-4 text-white" />
              <span>Saved!</span>
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              <span>Save Changes</span>
            </>
          )}
        </button>
      </div>

      <div className="space-y-3 font-mono">
        <div className="flex items-center justify-between p-3.5 bg-slate-950 border border-slate-800 rounded-xl">
          <div>
            <h4 className="font-bold text-slate-100 font-sans">SAML 2.0 Enterprise SSO Provider</h4>
            <p className="text-slate-400 text-[11px] font-sans">Shibboleth / Okta identity provider integration.</p>
          </div>
          <input
            type="checkbox"
            checked={sso.saml_enabled}
            onChange={(e) => {
              setSaved(false);
              setSso({ ...sso, saml_enabled: e.target.checked });
            }}
            className="w-4 h-4 rounded bg-slate-900 border-slate-700 text-indigo-600 focus:ring-indigo-500"
          />
        </div>

        <div className="flex items-center justify-between p-3.5 bg-slate-950 border border-slate-800 rounded-xl">
          <div>
            <h4 className="font-bold text-slate-100 font-sans">Google Workspace OAuth 2.0 Login</h4>
            <p className="text-slate-400 text-[11px] font-sans">Allow login via institutional Google accounts.</p>
          </div>
          <input
            type="checkbox"
            checked={sso.google_oauth}
            onChange={(e) => {
              setSaved(false);
              setSso({ ...sso, google_oauth: e.target.checked });
            }}
            className="w-4 h-4 rounded bg-slate-900 border-slate-700 text-indigo-600 focus:ring-indigo-500"
          />
        </div>

        <div className="flex items-center justify-between p-3.5 bg-slate-950 border border-slate-800 rounded-xl">
          <div>
            <h4 className="font-bold text-slate-100 font-sans">Microsoft Entra ID / Azure AD SSO</h4>
            <p className="text-slate-400 text-[11px] font-sans">Single Sign-On for Office 365 users.</p>
          </div>
          <input
            type="checkbox"
            checked={sso.microsoft_entra}
            onChange={(e) => {
              setSaved(false);
              setSso({ ...sso, microsoft_entra: e.target.checked });
            }}
            className="w-4 h-4 rounded bg-slate-900 border-slate-700 text-indigo-600 focus:ring-indigo-500"
          />
        </div>
      </div>
    </div>
  );
};
